import { useEffect, useState } from "react";
import { RoleBadge } from "@/components/Shared";
import { Trophy } from "lucide-react";

/** Overlay that appears for ~3.5s when a player is assigned, with fireworks emojis. */
export default function WinnerReveal({ event, onDone }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!event) return;
    setVisible(true);
    const t = setTimeout(() => { setVisible(false); onDone?.(); }, 3500);
    return () => clearTimeout(t);
  }, [event?.ts]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!event || !visible) return null;

  // Deterministic small "fireworks" positions
  const sparks = Array.from({ length: 14 });

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center" data-testid="winner-reveal">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm animate-[fadeIn_.25s_ease-out]" />
      <div className="absolute inset-0 overflow-hidden">
        {sparks.map((_, i) => (
          <span key={`spark-${i}`}
                className="absolute text-3xl sm:text-5xl"
                style={{
                  left: `${(i * 137) % 100}%`,
                  top: `${(i * 79) % 90}%`,
                  animation: `sparkPop 2.2s ${(i % 10) * 0.15}s ease-out both`,
                }}>
            {["🎉","🎆","✨","🎇","⭐"][i % 5]}
          </span>
        ))}
      </div>
      <div className="relative glass rounded-3xl px-8 sm:px-12 py-8 sm:py-10 text-center emerald-glow border-emerald-500/60 max-w-lg mx-4"
           style={{ animation: "revealPop .55s cubic-bezier(.34,1.56,.64,1) both" }}>
        <div className="flex items-center justify-center gap-2 mb-3">
          <Trophy className="w-6 h-6 text-emerald-400" />
          <div className="text-emerald-400 text-xs uppercase tracking-[0.35em] font-black">Aggiudicato!</div>
        </div>
        {event.role && (
          <div className="flex justify-center mb-3">
            <RoleBadge role={event.role} className="w-12 h-12 text-xl" />
          </div>
        )}
        <div className="text-3xl sm:text-4xl font-black tracking-tight mb-2" data-testid="winner-player-name">{event.player_name}</div>
        <div className="text-sm text-slate-400 mb-4">va a</div>
        <div className="text-2xl sm:text-3xl font-black text-emerald-400 mb-4" data-testid="winner-team-name">{event.team_name}</div>
        <div className="inline-flex items-baseline gap-2 px-5 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/40">
          <span className="font-mono text-4xl font-black text-emerald-300" data-testid="winner-amount">{event.amount}</span>
          <span className="text-sm text-slate-400 font-bold">FM</span>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes revealPop {
          0% { opacity: 0; transform: scale(.7) translateY(30px) }
          100% { opacity: 1; transform: scale(1) translateY(0) }
        }
        @keyframes sparkPop {
          0% { opacity: 0; transform: scale(.3) rotate(0deg); filter: drop-shadow(0 0 12px rgba(16,185,129,.7)); }
          25% { opacity: 1; transform: scale(1.3) rotate(20deg); }
          75% { opacity: 1; transform: scale(1.1) translateY(-15px) rotate(-15deg); }
          100% { opacity: 0; transform: scale(.6) translateY(-40px) rotate(30deg); }
        }
      `}</style>
    </div>
  );
}
